import { useEffect, useId, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Mail, Scale } from "lucide-react";
import { ApiError, useApi, type ApiClient } from "@ncp/api-client";
import { legalBasisText, useEnumLabel, useLocale, useTranslation } from "@ncp/i18n";
import { Badge, Button, Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, InlineAlert, MarkdownBody, Skeleton, cn, notifySaveFailed } from "@ncp/ui";
import type { LetterCitationHold } from "../api";
import { LetterCitationsNotice } from "../LetterCitationsNotice";

/** A letter a step would send, rendered by the server from the step's own template and the file, as the step will send it (release 1.6). */
export interface StepLetter {
  source: string; templateKey: string; kind: string; subject: string; subjectAr: string; body: string; bodyAr: string;
  recipientName: string; recipientNameAr?: string | null; legalEffect: boolean; legalBasis?: string | null; responseDays?: number | null; responseWorkingDays: boolean;
  /** The provisions it cites that the Department has not yet verified, named before the step is confirmed (condition 2.13); none when null. */
  citations?: LetterCitationHold | null;
}
/**
 * `stepAvailable` is false when the step cannot be taken now: it sends nothing, and taking it says why, as before. `fingerprint` is what the
 * step's confirmation carries once the letters are read; the server renders them again and refuses the step if they changed.
 */
interface StepLettersView { letters: StepLetter[]; stepAvailable: boolean; fingerprint?: string | null }

/** The step whose letters are read: a transition with the officer's reasons, a task whose completion takes a step, or an extension of a period. */
export type StepRequest = { trigger: string; note?: string | null } | { taskId: string } | { clockKey: string; days: number; justification: string };

/** The step itself, whatever the officer's wording: letters of the same step stay on screen while the new wording is rendered. */
const stepOf = (r: StepRequest) => ("trigger" in r ? `transition:${r.trigger}` : "taskId" in r ? `task:${r.taskId}` : `clock:${r.clockKey}`);
const stepLettersKey = (caseId: string | undefined, r: StepRequest) => ["step-letters", caseId, stepOf(r), JSON.stringify(r)] as const;

function fetchStepLetters(api: ApiClient, caseId: string, r: StepRequest) {
  if ("trigger" in r) return api.post<StepLettersView>(`/api/cases/${caseId}/workflow/transitions/${encodeURIComponent(r.trigger)}/letters`, { note: r.note ?? null });
  if ("taskId" in r) return api.get<StepLettersView>(`/api/cases/${caseId}/workflow/tasks/${r.taskId}/letters`);
  return api.post<StepLettersView>(`/api/cases/${caseId}/workflow/clocks/extend/letters`, r);
}

/**
 * The step to render once the officer's wording has stopped changing for a moment, so a letter quoting it is not rendered on every keystroke.
 * Another step (or none) is taken at once.
 */
function useSettled(current: string | null, ms = 450): string | null {
  const [settled, setSettled] = useState(current);
  useEffect(() => {
    if (current === settled) return;
    if (!current || !settled || stepOf(JSON.parse(current) as StepRequest) !== stepOf(JSON.parse(settled) as StepRequest)) { setSettled(current); return; }
    const id = window.setTimeout(() => setSettled(current), ms);
    return () => window.clearTimeout(id);
  }, [current, settled, ms]);
  return settled;
}

export interface StepLettersState {
  letters: StepLetter[];
  loading: boolean;
  /** The officer's latest wording is being rendered into the letters on screen. */
  updating: boolean;
  error: Error | null;
  /** The step may be confirmed: the letters on screen are those of the step as it now stands – at once for a step that sends none (Condition 3.10). */
  shown: boolean;
  /** The fingerprint of the letters on screen, for the confirmation to carry; null when the step sends none. */
  fingerprint: string | null;
}

/**
 * The letters a step would send to the parties, rendered by the server from the same templates and record the step sends them from, and nothing
 * sent or saved (release 1.6; Conditions 3.1, 3.2 and 3.10). Null while there is no step to read.
 */
export function useStepLetters(caseId: string | undefined, request: StepRequest | null): StepLettersState {
  const api = useApi();
  const current = request ? JSON.stringify(request) : null;
  const settled = useSettled(current);
  const asked = settled ? (JSON.parse(settled) as StepRequest) : null;
  const query = useQuery({
    queryKey: asked ? stepLettersKey(caseId, asked) : ["step-letters", caseId, null],
    queryFn: () => fetchStepLetters(api, caseId!, asked!),
    enabled: !!caseId && !!asked,
    placeholderData: (previous, previousQuery) => (asked && previousQuery?.queryKey[2] === stepOf(asked) ? previous : undefined),
    staleTime: 0,
    retry: false,
  });
  const sameStep = !!request && !!asked && stepOf(request) === stepOf(asked);
  const letters = request && query.data ? query.data.letters : [];
  // Letters read again (after the server found them changed) are confirmed only once the new rendering is on screen.
  const fresh = !!current && settled === current && query.isSuccess && !query.isPlaceholderData && !query.isFetching;
  // Whether a step sends letters does not turn on the officer's wording, so a step that sends none never waits for it to be rendered.
  const shown = !!request && !query.isError && !!query.data && sameStep && (fresh || query.data.letters.length === 0);
  return {
    letters, loading: !!request && !query.data && !query.isError, updating: letters.length > 0 && !fresh && !query.isError, error: request ? (query.error as Error | null) : null, shown,
    fingerprint: shown && letters.length > 0 ? query.data?.fingerprint ?? null : null,
  };
}

const REFUSALS = new Set(["LettersNotRead", "LettersChanged"]);

/**
 * When the server refused a step because its letters were not read, or changed since (release 1.6), the letters are read again; the save helper
 * has already reported the refusal in the server's words. Returns false for any other error.
 */
export function useStepLettersRefusal() {
  const qc = useQueryClient();
  return (e: unknown): boolean => {
    if (!(e instanceof ApiError) || !REFUSALS.has(e.problem?.code ?? "")) return false;
    void qc.invalidateQueries({ queryKey: ["step-letters"] });
    return true;
  };
}

/** True once <paramref name="on"/> has held for a moment: a quick answer shows no loading line. */
function useLater(on: boolean, ms = 400) {
  const [later, setLater] = useState(false);
  useEffect(() => {
    if (!on) { setLater(false); return; }
    const id = window.setTimeout(() => setLater(true), ms);
    return () => window.clearTimeout(id);
  }, [on, ms]);
  return later;
}

/** Each letter a step sends, in full, as it will be sent: what it is, its subject, addressee and legal effect, its English and Arabic text. Nothing when the step sends none. */
export function StepLetters({ state, className }: { state: StepLettersState; className?: string }) {
  const { t } = useTranslation();
  const id = useId();
  const slow = useLater(state.loading);
  if (state.error) return <InlineAlert tone="danger" title={t("backoffice:stepLetters.failed")} className={className}>{state.error.message}</InlineAlert>;
  if (state.loading) return slow ? <p className={cn("flex items-center gap-2 text-xs text-muted-foreground", className)}><Skeleton className="size-3.5 rounded-full" /> {t("backoffice:stepLetters.loading")}</p> : null;
  if (state.letters.length === 0) return null;
  return (
    <section className={cn("space-y-3", className)} aria-labelledby={id} data-step-letters={state.letters.length}>
      <div>
        <p id={id} className="flex items-center gap-1.5 text-sm font-semibold text-aeblack-950"><Mail className="size-4 text-sand-700" aria-hidden /> {t("backoffice:stepLetters.title", { count: state.letters.length })}</p>
        <p className="mt-0.5 text-xs text-muted-foreground">{t("backoffice:stepLetters.hint")}</p>
        {state.updating ? <p className="mt-0.5 text-[11px] text-sand-700" aria-live="polite">{t("backoffice:stepLetters.updating")}</p> : null}
      </div>
      {state.letters.map((l, i) => <StepLetterCard key={`${l.source}:${l.templateKey}:${i}`} letter={l} />)}
    </section>
  );
}

function StepLetterCard({ letter: l }: { letter: StepLetter }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const enumLabel = useEnumLabel();
  const ar = locale === "ar";
  const basis = legalBasisText(l.legalBasis, locale);
  return (
    <article className="overflow-hidden rounded-lg border border-sand-200 bg-white" data-step-letter={l.source}>
      <header className="space-y-1 border-b border-sand-100 bg-sand-50/60 px-3 py-2.5">
        <p className="text-[11px] font-semibold uppercase tracking-wider text-sand-600">{t(`backoffice:stepLetters.sources.${l.source}`, { defaultValue: enumLabel("letterKind", l.kind) })}</p>
        <p className="text-sm font-semibold text-aeblack-950" dir="auto">{ar ? l.subjectAr : l.subject}</p>
        <div className="flex flex-wrap items-center gap-1.5 text-xs text-sand-800">
          <span dir="auto">{t("backoffice:stepLetters.to", { name: ar && l.recipientNameAr ? l.recipientNameAr : l.recipientName })}</span>
          {l.legalEffect ? <Badge size="sm" variant="warning" title={t("backoffice:stepLetters.legalEffectHint")}><Scale aria-hidden /> {t("backoffice:stepLetters.legalEffect")}</Badge> : <Badge size="sm" variant="outline">{t("backoffice:stepLetters.courtesy")}</Badge>}
          <Badge size="sm" variant="outline">{enumLabel("letterKind", l.kind)}</Badge>
          {l.responseDays ? <Badge size="sm" variant="outline">{t("backoffice:stepLetters.reply", { period: t(l.responseWorkingDays ? "time.workingDays" : "time.days", { count: l.responseDays }) })}</Badge> : null}
        </div>
        {basis ? <p className="text-[11px] text-sand-600">{basis}</p> : null}
      </header>
      {l.citations ? <div className="border-b border-sand-100 px-3 py-2.5"><LetterCitationsNotice citations={l.citations} held={false} draft /></div> : null}
      <div className="grid divide-y divide-sand-100 md:grid-cols-2 md:divide-x md:divide-y-0 rtl:md:divide-x-reverse">
        <LetterText label={t("backoffice:stepLetters.english")} subject={l.subject} body={l.body} lang="en" dir="ltr" />
        <LetterText label={t("backoffice:stepLetters.arabic")} subject={l.subjectAr} body={l.bodyAr} lang="ar" dir="rtl" />
      </div>
    </article>
  );
}

function LetterText({ label, subject, body, lang, dir }: { label: string; subject: string; body: string; lang: string; dir: "ltr" | "rtl" }) {
  return (
    <div className="min-w-0 px-3 py-2.5">
      <p className="text-[11px] font-semibold uppercase tracking-wider text-sand-600">{label}</p>
      <div lang={lang} dir={dir}>
        <p className="mt-1 text-sm font-medium text-aeblack-950">{subject}</p>
        <div className="mt-1 max-h-72 overflow-y-auto text-sm scrollbar-thin" tabIndex={0} role="region" aria-label={`${label} · ${subject}`}><MarkdownBody>{body}</MarkdownBody></div>
      </div>
    </div>
  );
}

/**
 * The confirmation of a step taken without a screen of its own – completing a task that takes a step, a step from a card. When the step sends
 * letters, they are shown in full first and the step is confirmed only once they are; a step that sends none is taken at once, as before.
 */
export function useConfirmStep(caseId: string | undefined) {
  const api = useApi();
  const qc = useQueryClient();
  const { t } = useTranslation();
  const [pending, setPending] = useState<{ request: StepRequest; take: (fingerprint: string | null) => void } | null>(null);
  const [checking, setChecking] = useState(false);
  /** `take` carries out the step with the fingerprint of the letters the officer read (null when it sends none). */
  const run = async (request: StepRequest, take: (fingerprint: string | null) => void) => {
    if (!caseId) return;
    setChecking(true);
    try {
      const view = await qc.fetchQuery({ queryKey: stepLettersKey(caseId, request), queryFn: () => fetchStepLetters(api, caseId, request), staleTime: 0 });
      if (view.letters.length === 0) take(null);
      else setPending({ request, take });
    } catch (e) {
      notifySaveFailed(e, t("backoffice:stepLetters.failed"));
    } finally {
      setChecking(false);
    }
  };
  const dialog = <StepLettersDialog caseId={caseId} request={pending?.request ?? null} onCancel={() => setPending(null)} onConfirm={(fingerprint) => { pending?.take(fingerprint); setPending(null); }} />;
  return { run, checking, dialog };
}

function StepLettersDialog({ caseId, request, onCancel, onConfirm }: { caseId: string | undefined; request: StepRequest | null; onCancel: () => void; onConfirm: (fingerprint: string | null) => void }) {
  const { t } = useTranslation();
  const state = useStepLetters(caseId, request);
  return (
    <Dialog open={!!request} onOpenChange={(o) => !o && onCancel()}>
      <DialogContent size="xl">
        <DialogHeader>
          <DialogTitle>{t("backoffice:stepLetters.dialog.title")}</DialogTitle>
          <DialogDescription>{t("backoffice:stepLetters.dialog.description")}</DialogDescription>
        </DialogHeader>
        <div className="max-h-[60vh] overflow-y-auto scrollbar-thin"><StepLetters state={state} /></div>
        <DialogFooter>
          <Button variant="ghost" onClick={onCancel}>{t("actions.cancel")}</Button>
          <Button variant="gold" disabled={!state.shown} onClick={() => onConfirm(state.fingerprint)}>{t("backoffice:stepLetters.dialog.confirm")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
